import React, { useState, useEffect } from 'react';
import { companiesApi, notificationsApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { PaperPlaneTilt, Crown, CalendarBlank, MagnifyingGlass, Warning, CheckCircle, Eye, DownloadSimple, XCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function MassNotificationPage() {
  const [mandantes, setMandantes] = useState([]);
  const [clientId, setClientId] = useState('');
  const [day, setDay] = useState('');
  const [cc, setCc] = useState('');
  const [rows, setRows] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    companiesApi.getAll({ is_client: true, page_size: 500 }).then((res) => {
      const data = res.data?.items || res.data || [];
      setMandantes(Array.isArray(data) ? data : []);
    }).catch(() => toast.error('Error al cargar mandantes'));
  }, []);

  const selectedMandante = mandantes.find((m) => m.id === clientId);
  const withEmail = (rows || []).filter((r) => r.has_email);
  const withoutEmail = (rows || []).filter((r) => !r.has_email);

  const handlePreview = async () => {
    if (!clientId || !day) {
      toast.error('Selecciona mandante y día');
      return;
    }
    setLoadingPreview(true);
    setRows(null);
    try {
      const res = await notificationsApi.massPreview(clientId, day);
      setRows(res.data.workers || []);
      if ((res.data.workers || []).length === 0) {
        toast.info('No hay capacitaciones para ese mandante y día');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar la vista previa');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await notificationsApi.massSend(clientId, day, cc.trim() || undefined);
      const d = res.data;
      toast.success(`${d.notifications_sent} correo(s) enviado(s)${d.notifications_failed ? `, ${d.notifications_failed} fallido(s)` : ''}`);
      setConfirmOpen(false);
      handlePreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar los correos');
    } finally {
      setSending(false);
    }
  };

  const dayLabel = day ? new Date(`${day}T12:00:00`).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

  const handleViewEmail = async (worker) => {
    setEmailPreview({ worker, html: '', recipient_email: worker.email });
    setLoadingEmail(true);
    try {
      const res = await notificationsApi.massPreviewEmail(clientId, day, worker.worker_id);
      setEmailPreview({ worker, html: res.data.html, recipient_email: res.data.recipient_email });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar la vista del correo');
      setEmailPreview(null);
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await notificationsApi.massExport(clientId, day);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `notificacion_${selectedMandante?.name || 'mandante'}_${day}.xlsx`.replace(/[^A-Za-z0-9_.-]+/g, '_');
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Error al descargar el Excel');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="mass-notification-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2B4B8C] flex items-center gap-2">
          <PaperPlaneTilt size={28} weight="fill" /> Notificación Masiva
        </h1>
        <p className="text-[#2B4B8C]/60 text-sm mt-1">
          Envía las invitaciones de un mandante para un día específico. Cada trabajador recibe un solo correo con todas sus capacitaciones de ese día.
        </p>
      </div>

      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-[#2B4B8C]/60 flex items-center gap-1"><Crown size={14} /> Mandante</Label>
              <Select value={clientId} onValueChange={(v) => { setClientId(v); setRows(null); }}>
                <SelectTrigger className="border-[#2B4B8C]/15" data-testid="mandante-select">
                  <SelectValue placeholder="Selecciona un mandante" />
                </SelectTrigger>
                <SelectContent>
                  {mandantes.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#2B4B8C]/60 flex items-center gap-1"><CalendarBlank size={14} /> Día</Label>
              <Input
                type="date"
                value={day}
                onChange={(e) => { setDay(e.target.value); setRows(null); }}
                className="border-[#2B4B8C]/15"
                data-testid="day-input"
              />
            </div>
          </div>
          <div className="space-y-1.5 md:w-1/2">
            <Label className="text-xs text-[#2B4B8C]/60">Copia (CC) — opcional</Label>
            <Input
              type="email"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="correo@ejemplo.cl"
              className="border-[#2B4B8C]/15"
              data-testid="cc-input"
            />
          </div>
          <div>
            <Button
              onClick={handlePreview}
              disabled={loadingPreview || !clientId || !day}
              className="bg-[#2B4B8C] text-white hover:bg-[#233d75]"
              data-testid="preview-btn"
            >
              <MagnifyingGlass size={16} className="mr-1.5" />
              {loadingPreview ? 'Generando...' : 'Generar vista previa'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {rows !== null && (
        <Card className="bg-white border-[#2B4B8C]/10" data-testid="preview-matrix">
          <CardContent className="p-0">
            <div className="flex items-center justify-between p-4 border-b border-[#2B4B8C]/10 flex-wrap gap-3">
              <div>
                <p className="font-medium text-[#2B4B8C]">
                  Matriz de confirmación {selectedMandante ? `· ${selectedMandante.name}` : ''} {dayLabel ? `· ${dayLabel}` : ''}
                </p>
                <p className="text-xs text-[#2B4B8C]/50 mt-0.5">
                  {withEmail.length} trabajador(es) recibirán correo{withoutEmail.length ? ` · ${withoutEmail.length} sin correo registrado` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleExport}
                  disabled={exporting || rows.length === 0}
                  variant="outline"
                  className="border-[#2B4B8C]/20 text-[#2B4B8C] hover:bg-[#f8fafc]"
                  data-testid="export-excel-btn"
                >
                  <DownloadSimple size={16} className="mr-1.5" /> {exporting ? 'Descargando...' : 'Descargar Excel'}
                </Button>
                <Button
                  onClick={() => setConfirmOpen(true)}
                  disabled={withEmail.length === 0}
                  className="bg-[#2BBAA4] text-white hover:bg-[#239986]"
                  data-testid="open-send-confirm"
                >
                  <PaperPlaneTilt size={16} className="mr-1.5" /> Enviar correos ({withEmail.length})
                </Button>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                  <TableHead className="text-[#2B4B8C]/60">RUT</TableHead>
                  <TableHead className="text-[#2B4B8C]/60">Nombre</TableHead>
                  <TableHead className="text-[#2B4B8C]/60">Cargo</TableHead>
                  <TableHead className="text-[#2B4B8C]/60">Empresa</TableHead>
                  <TableHead className="text-[#2B4B8C]/60">Correo</TableHead>
                  <TableHead className="text-[#2B4B8C]/60 w-[340px]">Capacitaciones del día</TableHead>
                  <TableHead className="text-[#2B4B8C]/60 text-right">Vista correo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-[#2B4B8C]/40 py-8">
                      No hay capacitaciones para este mandante en el día seleccionado.
                    </TableCell>
                  </TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.worker_id} className="border-[#2B4B8C]/5 hover:bg-[#f8fafc] align-top" data-testid={`matrix-row-${r.worker_id}`}>
                    <TableCell className="text-[#2B4B8C]/70 font-mono text-sm">{r.rut || '-'}</TableCell>
                    <TableCell className="text-[#2B4B8C] font-medium">{r.name}</TableCell>
                    <TableCell className="text-[#2B4B8C]/70 text-sm">{r.cargo || '-'}</TableCell>
                    <TableCell className="text-[#2B4B8C]/70 text-sm">{r.company_name || '-'}</TableCell>
                    <TableCell>
                      {r.has_email ? (
                        <span className="text-[#2B4B8C]/70 text-sm">{r.email}</span>
                      ) : (
                        <span className="text-red-400 text-xs flex items-center gap-1"><Warning size={13} /> Sin correo</span>
                      )}
                    </TableCell>
                    <TableCell className="w-[340px] max-w-[340px]">
                      <div className="flex flex-wrap gap-1.5">
                        {r.trainings.map((t, i) => (
                          <Badge key={i} className="bg-[#2B4B8C]/8 text-[#2B4B8C] border border-[#2B4B8C]/15 text-xs font-normal whitespace-normal break-words">
                            {t}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleViewEmail(r)}
                        className="text-[#2B4B8C]/70 hover:text-[#2B4B8C]"
                        title="Ver cómo llegaría el correo"
                        data-testid={`view-email-${r.worker_id}`}
                      >
                        <Eye size={16} className="mr-1" /> Ver correo
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {emailPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEmailPreview(null)} data-testid="email-preview-overlay">
          <Card className="w-full max-w-3xl max-h-[90vh] bg-white border-[#2B4B8C]/10 shadow-lg flex flex-col" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-5 flex flex-col gap-3 overflow-hidden">
              <div className="flex items-center justify-between gap-3">
                <p className="font-medium text-[#2B4B8C] text-lg flex items-center gap-2"><Eye size={20} /> Vista previa del correo</p>
                <Button variant="ghost" size="sm" onClick={() => setEmailPreview(null)} className="text-[#2B4B8C]/60" data-testid="close-email-preview"><XCircle size={20} /></Button>
              </div>
              <div className="text-xs text-[#2B4B8C]/60">
                Para: <strong className="text-[#2B4B8C]">{emailPreview.worker.name}</strong> · {emailPreview.recipient_email}
              </div>
              <div className="border border-[#2B4B8C]/10 rounded-lg overflow-auto bg-[#f4f6fb]" style={{ height: '60vh' }}>
                {loadingEmail ? (
                  <div className="flex items-center justify-center h-full text-[#2B4B8C]/50 text-sm">Generando vista previa...</div>
                ) : (
                  <iframe title="email-preview" srcDoc={emailPreview.html} className="w-full h-full border-0" data-testid="email-preview-iframe" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !sending && setConfirmOpen(false)} data-testid="send-confirm-overlay">
          <Card className="w-[440px] bg-white border-[#2B4B8C]/10 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6 space-y-4">
              <p className="font-medium text-[#2B4B8C] text-lg flex items-center gap-2"><PaperPlaneTilt size={20} /> Confirmar envío</p>
              <p className="text-sm text-[#2B4B8C]/70">
                Se enviará un correo consolidado a <strong>{withEmail.length} trabajador(es)</strong> del mandante
                <strong> {selectedMandante?.name}</strong> para el día <strong>{dayLabel}</strong>.
              </p>
              {cc.trim() && (
                <p className="text-xs text-[#2B4B8C]/60 flex items-center gap-1">
                  <CheckCircle size={13} className="text-green-500" /> Con copia (CC) a: {cc.trim()}
                </p>
              )}
              {withoutEmail.length > 0 && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <Warning size={13} /> {withoutEmail.length} trabajador(es) sin correo serán omitidos.
                </p>
              )}
              <div className="flex gap-2 justify-end pt-1">
                <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending} className="border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="cancel-send">Cancelar</Button>
                <Button onClick={handleSend} disabled={sending} className="bg-[#2BBAA4] text-white hover:bg-[#239986]" data-testid="confirm-send">
                  {sending ? 'Enviando...' : 'Enviar correos'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
